import { useFormikContext } from 'formik';
import React, { useMemo, useState, useEffect } from 'react';
import TextInput from '../../../BaseComponents/TextInput';
import { ReactComponent as ArrowIcon } from '../../../../assets/icons/arrow-down.svg';

interface GenderValuesBlockProps {
  dataIndex: number;
  itemIndex: number;
}

export const AgesGenderValuesBlock: React.FC<GenderValuesBlockProps> = ({
  dataIndex,
  itemIndex
}) => {
  const [expanded, setExpanded] = useState(true);
  const [isInfiniteLowTo, setIsInfiniteLowTo] = useState(false);
  const [isInfiniteBelowOptimalTo, setIsInfiniteBelowOptimalTo] = useState(false);
  const [isInfiniteOptimalTo, setIsInfiniteOptimalTo] = useState(false);
  const [isInfiniteAboveOptimalTo, setIsInfiniteAboveOptimalTo] = useState(false);
  const [isInfiniteHighTo, setIsInfiniteHighTo] = useState(false);
  const { setFieldValue, values } = useFormikContext<any>();
  const ageData = values.genderData[dataIndex]?.data_with_age[itemIndex];
  const lowToInfinity = ageData?.low_to_infinity;
  const belowOptimalToInfinity = ageData?.below_optimal_to_infinity;
  const optimalToInfinity = ageData?.optimal_to_infinity;
  const aboveOptimalToInfinity = ageData?.above_optimal_to_infinity;
  const highToInfinity = ageData?.high_to_infinity;

  useEffect(() => {
    setIsInfiniteLowTo(lowToInfinity === true);
    setIsInfiniteBelowOptimalTo(belowOptimalToInfinity === true);
    setIsInfiniteOptimalTo(optimalToInfinity === true);
    setIsInfiniteAboveOptimalTo(aboveOptimalToInfinity === true);
    setIsInfiniteHighTo(highToInfinity === true);
  }, [
    lowToInfinity,
    belowOptimalToInfinity,
    optimalToInfinity,
    aboveOptimalToInfinity,
    highToInfinity,
  ]);

  const entry = values.genderData[dataIndex].data_with_age[itemIndex];

  const setField = (field: string, value: string) => {
    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].${field}`,
      value
    );
  };

  const handleDeleteBlock = () => {
    setFieldValue(
      `genderData[${dataIndex}].data_with_age`,
      values.genderData[dataIndex].data_with_age.filter(
        (_: any, i: number) => i !== itemIndex
      )
    );
  };

  const onDisableRange = (
    e: React.ChangeEvent<HTMLInputElement>,
    keys: string[]
  ) => {
    const isDisabling = e.target.checked;
    const val = isDisabling ? 'NA' : '';

    let hadInfinity = false;
    if (keys.includes('low_to') && isInfiniteLowTo) hadInfinity = true;
    if (keys.includes('below_optimal_to') && isInfiniteBelowOptimalTo) hadInfinity = true;
    if (keys.includes('optimal_to') && isInfiniteOptimalTo) hadInfinity = true;
    if (keys.includes('above_optimal_to') && isInfiniteAboveOptimalTo) hadInfinity = true;
    if (keys.includes('high_to') && isInfiniteHighTo) hadInfinity = true;

    if (isDisabling && hadInfinity) {
      const currentData = values.genderData[dataIndex].data_with_age[itemIndex];
      if (!keys.includes('low_to') && currentData.low_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].low_from`, '');
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].low_to`, '');
      }
      if (!keys.includes('below_optimal_to') && currentData.below_optimal_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].below_optimal_from`, '');
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].below_optimal_to`, '');
      }
      if (!keys.includes('optimal_to') && currentData.optimal_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].optimal_from`, '');
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].optimal_to`, '');
      }
      if (!keys.includes('above_optimal_to') && currentData.above_optimal_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].above_optimal_from`, '');
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].above_optimal_to`, '');
      }
      if (!keys.includes('high_to') && currentData.high_from === 'NA') {
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].high_from`, '');
        setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].high_to`, '');
      }
    }

    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].${keys[0]}`,
      val
    );
    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].${keys[1]}`,
      val
    );

    if (keys.includes('low_to')) {
      setIsInfiniteLowTo(false);
      setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].low_to_infinity`, false);
    }
    if (keys.includes('below_optimal_to')) {
      setIsInfiniteBelowOptimalTo(false);
      setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].below_optimal_to_infinity`, false);
    }
    if (keys.includes('optimal_to')) {
      setIsInfiniteOptimalTo(false);
      setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].optimal_to_infinity`, false);
    }
    if (keys.includes('above_optimal_to')) {
      setIsInfiniteAboveOptimalTo(false);
      setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].above_optimal_to_infinity`, false);
    }
    if (keys.includes('high_to')) {
      setIsInfiniteHighTo(false);
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].high_to`,
        val
      );
      setFieldValue(`genderData[${dataIndex}].data_with_age[${itemIndex}].high_to_infinity`, false);
    }
  };

  const useRangeValidation = (
    valA: string,
    valB: string,
    compareFn: (a: number, b: number) => boolean
  ) => {
    return useMemo(() => {
      if (valA === '' || valB === '' || valA === '∞') return false;
      return compareFn(Number(valA), Number(valB));
    }, [valA, valB, compareFn]);
  };

  const isCriticalLowToError = useRangeValidation(
    entry.low_to,
    entry.low_from,
    (a, b) => a <= b
  );

  const isLowFromError = useRangeValidation(
    entry.below_optimal_from,
    entry.low_to,
    (a, b) => a <= b
  );

  const isLowToError = useRangeValidation(
    entry.below_optimal_to,
    entry.below_optimal_from,
    (a, b) => a <= b
  );

  const isOptimalFromError = useRangeValidation(
    entry.optimal_from,
    entry.below_optimal_to,
    (a, b) => a <= b
  );

  const isOptimalToError = useRangeValidation(
    entry.optimal_to,
    entry.optimal_from,
    (a, b) => a <= b
  );

  const isHighFromError = useRangeValidation(
    entry.above_optimal_from,
    entry.optimal_to,
    (a, b) => a <= b
  );

  const isHighToError = useRangeValidation(
    entry.above_optimal_to,
    entry.above_optimal_from,
    (a, b) => a <= b
  );

  const isCriticalHighFromError = useRangeValidation(
    entry.high_from,
    entry.above_optimal_to,
    (a, b) => a <= b
  );

  const isCriticalHighToError = useRangeValidation(
    entry.high_to,
    entry.high_from,
    (a, b) => a <= b
  );

  const onInfiniteLowToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteLowTo(isChecked);
    if (isChecked) {
      setField('low_to', '\u221e');
      setField('below_optimal_from', 'NA');
      setField('below_optimal_to', 'NA');
      setField('optimal_from', 'NA');
      setField('optimal_to', 'NA');
      setField('above_optimal_from', 'NA');
      setField('above_optimal_to', 'NA');
      setField('high_from', 'NA');
      setField('high_to', 'NA');
      setIsInfiniteBelowOptimalTo(false);
      setIsInfiniteOptimalTo(false);
      setIsInfiniteAboveOptimalTo(false);
      setIsInfiniteHighTo(false);
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].below_optimal_to_infinity`,
        false
      );
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].optimal_to_infinity`,
        false
      );
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].above_optimal_to_infinity`,
        false
      );
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].high_to_infinity`,
        false
      );
    } else {
      setField('low_to', '');
      const ageData = values.genderData[dataIndex].data_with_age[itemIndex];
      if (ageData.below_optimal_from === 'NA') setField('below_optimal_from', '');
      if (ageData.below_optimal_to === 'NA') setField('below_optimal_to', '');
      if (ageData.optimal_from === 'NA') setField('optimal_from', '');
      if (ageData.optimal_to === 'NA') setField('optimal_to', '');
      if (ageData.above_optimal_from === 'NA') setField('above_optimal_from', '');
      if (ageData.above_optimal_to === 'NA') setField('above_optimal_to', '');
      if (ageData.high_from === 'NA') setField('high_from', '');
      if (ageData.high_to === 'NA') setField('high_to', '');
    }
    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].low_to_infinity`,
      isChecked
    );
  };

  const onInfiniteBelowOptimalToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteBelowOptimalTo(isChecked);
    if (isChecked) {
      setField('below_optimal_to', '∞');
      setField('optimal_from', 'NA');
      setField('optimal_to', 'NA');
      setField('above_optimal_from', 'NA');
      setField('above_optimal_to', 'NA');
      setField('high_from', 'NA');
      setField('high_to', 'NA');
      setIsInfiniteOptimalTo(false);
      setIsInfiniteAboveOptimalTo(false);
      setIsInfiniteHighTo(false);
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].optimal_to_infinity`,
        false
      );
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].above_optimal_to_infinity`,
        false
      );
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].high_to_infinity`,
        false
      );
    } else {
      setField('below_optimal_to', '');
      const ageData = values.genderData[dataIndex].data_with_age[itemIndex];
      if (ageData.optimal_from === 'NA') setField('optimal_from', '');
      if (ageData.optimal_to === 'NA') setField('optimal_to', '');
      if (ageData.above_optimal_from === 'NA') setField('above_optimal_from', '');
      if (ageData.above_optimal_to === 'NA') setField('above_optimal_to', '');
      if (ageData.high_from === 'NA') setField('high_from', '');
      if (ageData.high_to === 'NA') setField('high_to', '');
    }
    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].below_optimal_to_infinity`,
      isChecked
    );
  };

  const onInfiniteOptimalToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteOptimalTo(isChecked);
    if (isChecked) {
      setField('optimal_to', '∞');
      setField('above_optimal_from', 'NA');
      setField('above_optimal_to', 'NA');
      setField('high_from', 'NA');
      setField('high_to', 'NA');
      setIsInfiniteAboveOptimalTo(false);
      setIsInfiniteHighTo(false);
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].above_optimal_to_infinity`,
        false
      );
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].high_to_infinity`,
        false
      );
    } else {
      setField('optimal_to', '');
      const ageData = values.genderData[dataIndex].data_with_age[itemIndex];
      if (ageData.above_optimal_from === 'NA') setField('above_optimal_from', '');
      if (ageData.above_optimal_to === 'NA') setField('above_optimal_to', '');
      if (ageData.high_from === 'NA') setField('high_from', '');
      if (ageData.high_to === 'NA') setField('high_to', '');
    }
    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].optimal_to_infinity`,
      isChecked
    );
  };

  const onInfiniteAboveOptimalToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteAboveOptimalTo(isChecked);
    if (isChecked) {
      setField('above_optimal_to', '∞');
      setField('high_from', 'NA');
      setField('high_to', 'NA');
      setIsInfiniteHighTo(false);
      setFieldValue(
        `genderData[${dataIndex}].data_with_age[${itemIndex}].high_to_infinity`,
        false
      );
    } else {
      setField('above_optimal_to', '');
      const ageData = values.genderData[dataIndex].data_with_age[itemIndex];
      if (ageData.high_from === 'NA') setField('high_from', '');
      if (ageData.high_to === 'NA') setField('high_to', '');
    }
    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].above_optimal_to_infinity`,
      isChecked
    );
  };

  const onInfiniteHighToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsInfiniteHighTo(isChecked);
    if (isChecked) {
      setField('high_to', '∞');
    } else {
      setField('high_to', '');
    }
    setFieldValue(
      `genderData[${dataIndex}].data_with_age[${itemIndex}].high_to_infinity`,
      isChecked
    );
  };

  return (
    <div className='gender-values-block'>
      <div className='gender-values-block-header'>
        <div
          className='gender-values-label'
          onClick={() => setExpanded(!expanded)}
        >
          <ArrowIcon
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }}
          />
          <span>{`Age dependent (${entry.age_from} - ${entry.age_to})`}</span>
        </div>
        <button
          className='gender-values-delete'
          disabled={values.genderData[dataIndex].data_with_age.length <= 1}
          onClick={(e) => {
            e.preventDefault();
            handleDeleteBlock();
          }}
        >
          Remove
        </button>
      </div>
      {expanded && (
        <div className='critical-values-container inner'>
          <div className='values-row'>
            <TextInput
              title='Age from'
              name='age_from'
              placeholder='from'
              type='number'
              value={
                values.genderData[dataIndex].data_with_age[itemIndex].age_from
              }
              onChange={(e) => setField('age_from', e.target.value)}
            />
            <span className='critical-values-separator'>-</span>
            <TextInput
              title='Age to'
              name='age_to'
              placeholder='to'
              type='number'
              value={
                values.genderData[dataIndex].data_with_age[itemIndex].age_to
              }
              onChange={(e) => setField('age_to', e.target.value)}
            />
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Low *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .low_from,
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .low_to
                    ].includes('NA')}
                    disabled={false}
                    onChange={(e) =>
                      onDisableRange(e, [
                        'low_from',
                        'low_to'
                      ])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='low_from'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .low_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .low_from === 'NA'
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .low_from !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .low_from
                    : ''
                }
                onChange={(e) => setField('low_from', e.target.value)}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteLowTo}
                    onChange={onInfiniteLowToChange}
                    disabled={values.genderData[dataIndex].data_with_age[itemIndex].low_from === 'NA' && values.genderData[dataIndex].data_with_age[itemIndex].low_to === 'NA'}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='low_to'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .low_to === 'NA'
                    ? 'NA'
                    : isInfiniteLowTo
                      ? '∞'
                      : 'to'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .low_to === 'NA' || isInfiniteLowTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .low_to !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .low_to
                    : ''
                }
                onChange={(e) => setField('low_to', e.target.value)}
                isError={isCriticalLowToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Below Optimal *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .below_optimal_from,
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .below_optimal_to
                    ].includes('NA')}
                    disabled={isInfiniteLowTo}
                    onChange={(e) => onDisableRange(e, ['below_optimal_from', 'below_optimal_to'])}
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='below_optimal_from'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .below_optimal_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .below_optimal_from === 'NA' || isInfiniteLowTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .below_optimal_from !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .below_optimal_from
                    : ''
                }
                onChange={(e) => setField('below_optimal_from', e.target.value)}
                isError={isLowFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteBelowOptimalTo}
                    onChange={onInfiniteBelowOptimalToChange}
                    disabled={(values.genderData[dataIndex].data_with_age[itemIndex].below_optimal_from === 'NA' && values.genderData[dataIndex].data_with_age[itemIndex].below_optimal_to === 'NA') || isInfiniteLowTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='below_optimal_to'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex].below_optimal_to ===
                    'NA'
                    ? 'NA'
                    : isInfiniteBelowOptimalTo
                      ? '∞'
                      : 'to'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex].below_optimal_to ===
                  'NA' || isInfiniteBelowOptimalTo || isInfiniteLowTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex].below_optimal_to !==
                    'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex].below_optimal_to
                    : ''
                }
                onChange={(e) => setField('below_optimal_to', e.target.value)}
                isError={isLowToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Optimal *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .optimal_from === 'NA' &&
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .optimal_to
                    ].includes('NA')}
                    disabled={isInfiniteLowTo || isInfiniteBelowOptimalTo}
                    onChange={(e) =>
                      onDisableRange(e, ['optimal_from', 'optimal_to'])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='optimal_from'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .optimal_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .optimal_from === 'NA' || isInfiniteLowTo || isInfiniteBelowOptimalTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .optimal_from !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .optimal_from
                    : ''
                }
                onChange={(e) => setField('optimal_from', e.target.value)}
                isError={isOptimalFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteOptimalTo}
                    onChange={onInfiniteOptimalToChange}
                    disabled={(values.genderData[dataIndex].data_with_age[itemIndex].optimal_from === 'NA' && values.genderData[dataIndex].data_with_age[itemIndex].optimal_to === 'NA') || isInfiniteLowTo || isInfiniteBelowOptimalTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='optimal_to'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .optimal_to === 'NA'
                    ? 'NA'
                    : isInfiniteOptimalTo
                      ? '∞'
                      : 'to'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .optimal_to === 'NA' || isInfiniteOptimalTo || isInfiniteLowTo || isInfiniteBelowOptimalTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .optimal_to !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .optimal_to
                    : ''
                }
                onChange={(e) => setField('optimal_to', e.target.value)}
                isError={isOptimalToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                Above Optimal *
                <label>
                  <input
                    type='checkbox'
                    checked={[
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .above_optimal_from === 'NA' &&
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .above_optimal_to
                    ].includes('NA')}
                    disabled={isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo}
                    onChange={(e) =>
                      onDisableRange(e, ['above_optimal_from', 'above_optimal_to'])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='above_optimal_from'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .above_optimal_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .above_optimal_from === 'NA' || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .above_optimal_from !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .above_optimal_from
                    : ''
                }
                onChange={(e) => setField('above_optimal_from', e.target.value)}
                isError={isHighFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className="input-label" >
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteAboveOptimalTo}
                    onChange={onInfiniteAboveOptimalToChange}
                    disabled={(values.genderData[dataIndex].data_with_age[itemIndex].above_optimal_from === 'NA' && values.genderData[dataIndex].data_with_age[itemIndex].above_optimal_to === 'NA') || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='above_optimal_to'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .above_optimal_to === 'NA'
                    ? 'NA'
                    : isInfiniteAboveOptimalTo
                      ? '∞'
                      : 'to'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .above_optimal_to === 'NA' || isInfiniteAboveOptimalTo || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .above_optimal_to !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .above_optimal_to
                    : ''
                }
                onChange={(e) => setField('above_optimal_to', e.target.value)}
                isError={isHighToError}
              />
            </div>
          </div>
          <div className='values-row'>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                High *
                <label>
                  <input
                    type='checkbox'
                    checked={values.genderData[dataIndex].data_with_age[itemIndex]
                      .high_from === 'NA' &&
                      values.genderData[dataIndex].data_with_age[itemIndex]
                        .high_to === 'NA'
                    }
                    disabled={isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo}
                    onChange={(e) =>
                      onDisableRange(e, [
                        'high_from', 'high_to'
                      ])
                    }
                  />
                  <span>Disable</span>
                </label>
              </div>
              <TextInput
                name='high_from'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .high_from === 'NA'
                    ? 'NA'
                    : 'from'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .high_from === 'NA' || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .high_from !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .high_from
                    : ''
                }
                onChange={(e) => setField('high_from', e.target.value)}
                isError={isCriticalHighFromError}
              />
            </div>
            <span className='critical-values-separator'>-</span>
            <div style={{ width: '100%' }}>
              <div className='input-label'>
                <label className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    checked={isInfiniteHighTo}
                    onChange={onInfiniteHighToChange}
                    disabled={(values.genderData[dataIndex].data_with_age[itemIndex].high_from === 'NA' && values.genderData[dataIndex].data_with_age[itemIndex].high_to === 'NA') || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo}
                  />
                  <span>Greater than</span>
                </label>
              </div>
              <TextInput
                name='high_to'
                placeholder={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .high_to === 'NA'
                    ? 'NA'
                    : isInfiniteHighTo
                      ? '∞'
                      : 'to'
                }
                isDisabled={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .high_to === 'NA' || isInfiniteHighTo || isInfiniteLowTo || isInfiniteBelowOptimalTo || isInfiniteOptimalTo || isInfiniteAboveOptimalTo
                }
                type='number'
                value={
                  values.genderData[dataIndex].data_with_age[itemIndex]
                    .high_to !== 'NA'
                    ? values.genderData[dataIndex].data_with_age[itemIndex]
                      .high_to
                    : ''
                }
                onChange={(e) => setField('high_to', e.target.value)}
                isError={isCriticalHighToError}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgesGenderValuesBlock;
